/**
 * Celebration for a completed task (SPEC.md §8.4).
 *
 * §8.4 is unusually insistent that this matters: "Sweeping the list to set off a run of
 * small celebrations is the point — it makes the list something worth revisiting, and
 * wanting to add to it is exactly the behaviour worth reinforcing."
 *
 * A burst is **layered by activation cost**. A 1 gets a modest grey puff; a 5 gets five
 * layers stacked — grey, green, blue, magenta, orange — each larger than the last. Finishing
 * something expensive should look like it cost something, and the colours are the same ramp
 * the dice use, so the connection reads without explanation.
 *
 * Mechanically this stays inside §8.4's rules. Particles are absolutely positioned in a
 * fixed overlay that never accepts pointer events, and they animate **transform and opacity
 * only** — nothing here can reflow the page underneath. `prefers-reduced-motion` skips the
 * whole thing rather than shortening it: the motion *is* the feature, so a token version
 * would be noise without the payoff.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { ENERGY_COLORS } from "./energyPips";
import { FireworksContext, type BurstFn } from "./fireworksContext";

/**
 * Particles alive at once.
 *
 * Ours, not the browser's — there is no platform limit here, only what a phone can composite
 * smoothly. These are position-fixed spans animating transform and opacity, which the
 * compositor handles without touching layout, so the ceiling is generous. It exists to stop
 * a fast sweep down a long list accumulating without bound, not to be frugal.
 */
const MAX_PARTICLES = 1600;

type Particle = {
  id: number;
  x: number;
  y: number;
  dx: number;
  dy: number;
  size: number;
  colour: string;
  duration: number;
  delay: number;
};

let nextId = 0;

function prefersReducedMotion(): boolean {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
}

/**
 * Build one burst.
 *
 * `energy` is the number of layers. Unrated tasks get a single modest layer — unknown cost
 * is not zero cost, but it is not a reason to throw a parade either.
 */
function buildBurst(x: number, y: number, energy: number | null, scale = 1): Particle[] {
  const layers = energy === null ? 1 : Math.max(1, Math.min(energy, 5));
  const particles: Particle[] = [];

  for (let layer = 1; layer <= layers; layer++) {
    const colour = ENERGY_COLORS[layer] ?? ENERGY_COLORS[1];
    // Scale is how a project gets to be a different order of thing without changing what
    // finishing a task looks like.
    const count = Math.round((7 + layer * 3) * Math.min(scale, 3));
    const reach = (52 + layer * 40) * scale;

    for (let i = 0; i < count; i++) {
      // Spread evenly, then jitter, so a burst looks scattered rather than like a clock face.
      const angle = (i / count) * Math.PI * 2 + Math.random() * 0.5;
      const distance = reach * (0.55 + Math.random() * 0.65);

      particles.push({
        id: nextId++,
        x,
        y,
        dx: Math.cos(angle) * distance,
        // Slight upward bias, then gravity in the keyframes — it reads as a firework
        // rather than an explosion.
        dy: Math.sin(angle) * distance - reach * 0.18,
        size: (3 + layer * 0.9 + Math.random() * 2) * Math.min(scale, 2),
        colour,
        // ~60% slower than the first pass. The original was over before the eye had
        // finished travelling to it, which wasted the reward it exists to deliver.
        duration: 830 + layer * 145 + Math.random() * 350,
        // Outer layers land fractionally later, so a big burst blooms outward.
        delay: (layer - 1) * 90,
      });
    }
  }
  return particles;
}

export function FireworksProvider({ children }: { children: ReactNode }) {
  const [particles, setParticles] = useState<Particle[]>([]);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach((t) => window.clearTimeout(t));
  }, []);

  const burst = useCallback<BurstFn>((x, y, energy, scale = 1) => {
    if (prefersReducedMotion()) return;

    const fresh = buildBurst(x, y, energy, scale);
    setParticles((current) => [...current, ...fresh].slice(-MAX_PARTICLES));

    // Reap by id rather than by count: bursts overlap, and slicing the array would drop
    // whichever particles happened to be oldest rather than the ones that have finished.
    const longest = Math.max(...fresh.map((p) => p.duration + p.delay));
    const ids = new Set(fresh.map((p) => p.id));
    const timer = window.setTimeout(() => {
      setParticles((current) => current.filter((p) => !ids.has(p.id)));
    }, longest + 60);
    timers.current.push(timer);
  }, []);

  return (
    <FireworksContext.Provider value={burst}>
      {children}
      <div className="fireworks" aria-hidden="true">
        {particles.map((p) => (
          <span
            key={p.id}
            className="spark"
            style={
              {
                left: `${p.x}px`,
                top: `${p.y}px`,
                width: `${p.size}px`,
                height: `${p.size}px`,
                background: p.colour,
                animationDuration: `${p.duration}ms`,
                animationDelay: `${p.delay}ms`,
                "--dx": `${p.dx}px`,
                "--dy": `${p.dy}px`,
              } as React.CSSProperties
            }
          />
        ))}
      </div>
    </FireworksContext.Provider>
  );
}
